import { useState } from 'react'
import { Link, useNavigate } from 'react-router'

import { AUTH_INPUT } from '@/components/auth-layout'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

import { authClient } from './auth-client'
import {
  CAPTCHA_FAILED,
  captchaHeaders,
  TurnstileWidget
} from './turnstile-widget'

export function ForgotPasswordForm() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  async function handleSubmit(event: React.SyntheticEvent) {
    event.preventDefault()
    setSubmitting(true)
    setError('')

    const captcha = await captchaHeaders()
    if (!captcha) {
      setSubmitting(false)
      setError(CAPTCHA_FAILED)
      return
    }
    const { error: authError } = await authClient.emailOtp.requestPasswordReset(
      { email },
      captcha
    )

    setSubmitting(false)
    if (authError) {
      setError(authError.message ?? 'Something went wrong. Please try again.')
      return
    }
    /** the request returns success even for an unknown email (no enumeration
     * leak), so route everyone to the code-entry step. email rides in history
     * state so it never lands in the URL. */
    void navigate('/reset-password', { replace: true, state: { email } })
  }

  return (
    <div className='mx-auto w-full max-w-[420px]'>
      <h1 className='font-display text-[36px] leading-[1.1] tracking-[-0.5px] text-ink'>
        Forgot password?
      </h1>
      <p className='mt-2 text-[15px] text-ink-soft'>
        Enter your email and we&apos;ll send you a code to reset it.
      </p>

      <form onSubmit={handleSubmit} className='mt-8 flex flex-col gap-4'>
        <div className='flex flex-col gap-1.5'>
          <Label htmlFor='email'>Email</Label>
          <Input
            id='email'
            type='email'
            autoComplete='email'
            placeholder='you@company.com'
            value={email}
            onChange={event => setEmail(event.target.value)}
            className={AUTH_INPUT}
          />
        </div>
        <TurnstileWidget />
        {error && <p className='text-sm text-red-600'>{error}</p>}
        <button
          type='submit'
          disabled={email.length === 0 || submitting}
          className='mt-2 w-full rounded-xl bg-accent py-3.5 text-[15px] font-medium text-white transition-all hover:-translate-y-px hover:bg-[#15296b] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:bg-accent'
        >
          {submitting ? 'Sending...' : 'Send reset code'}
        </button>
      </form>

      <p className='mt-8 text-center text-sm text-ink-soft'>
        Remembered it?{' '}
        <Link
          to='/login'
          className='font-medium text-accent dark:text-accent-mid'
        >
          Login
        </Link>
      </p>
    </div>
  )
}
